import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { NavBar } from 'components/NavBar/NavBar'
import { Flex, PageWrapper, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { BodyText, Head1, OtherText, TitleText } from 'styling/Typography'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button } from 'components/ui/Button/Button'
import { NoData } from 'components/NoData'
import { tokens } from 'styling/tokens'
import { IOpsShiftReviewEntry, IOpsShiftScorecardRow } from 'types/opsAnalytics.interface'
import { formatNumbersForDisplay } from 'utils/helpers'
import { USE_OPS_MOCKS } from '../Shared/opsControl'
import { OPS_SHIFT_REVIEWS, opsDateLabelOf } from '../Shared/opsAnalyticsData'
import { DemoBadge } from '../Shared/OpsSharedComponents'
import { LossWaterfall } from './LossWaterfall'
import { TonnesPct } from './DayReports.styled'
import {
  ExceptionDot,
  ExceptionList,
  ExceptionRow,
  ExceptionTime,
  InfoNote,
  LockNoteWrap,
  OpenLink,
  PeopleBullet,
  PeopleList,
  PeopleRow,
  ReviewCell,
  ReviewId,
  ReviewStack,
  SubtitleWrap,
} from './ShiftReview.styled'

const withUnit = (value: string, unit: string | null): string => {
  if (!unit) return value
  if (unit === '%') return `${value}%`
  return `${value} ${unit}`
}

const DAY_AVG_NOTE = 'Compared against the last 5 day shifts — never against night shifts.'

const SHIFT_PILLS: IOpsShiftReviewEntry['shiftName'][] = ['Day Shift', 'Night Shift']

const PICKER_MIN_DATE = new Date(new Date().getFullYear(), 5, 2)

const TOP_LOADER_PREFIX = 'Top loader:'

const tonnesTone = (tonnes: number, target: number): 'success' | 'warning' | 'danger' => {
  const deficit = (target - tonnes) / target
  if (deficit > 0.15) return 'danger'
  if (deficit > 0.05) return 'warning'
  return 'success'
}

const scorecardColumns: IHonestTableColumn<IOpsShiftScorecardRow & { id: number }>[] = [
  {
    id: 'metric',
    header: 'Metric',
    cell: (item) => (
      <Flex column gap='0.25rem'>
        <BodyText weight='600'>{item.label}</BodyText>
        {item.note && <OtherText grey>{item.note}</OtherText>}
      </Flex>
    ),
  },
  {
    id: 'result',
    header: 'Result',
    cell: (item) => (
      <BodyText weight='700' color={tokens.status[item.tone].fg}>
        {withUnit(item.value, item.unit)}
      </BodyText>
    ),
  },
  {
    id: 'target',
    header: 'Target',
    cell: (item) => <OtherText grey>{item.target ? withUnit(item.target, item.unit) : '—'}</OtherText>,
  },
  {
    id: 'avg5',
    header: '5-shift avg',
    cell: (item) => <OtherText grey>{item.avg5 ? withUnit(item.avg5, item.unit) : '—'}</OtherText>,
  },
]

export const ShiftReview = () => {
  const navigate = useNavigate()
  const [dateLabel, setDateLabel] = useState('Yesterday')
  const [shiftName, setShiftName] = useState<IOpsShiftReviewEntry['shiftName']>('Day Shift')
  const [pickedDate, setPickedDate] = useState<Date | null>(null)

  const entry =
    OPS_SHIFT_REVIEWS.find((e) => e.dateLabel === dateLabel && e.shiftName === shiftName) ?? null

  const scorecardRows = entry
    ? entry.pack.scorecard.map((row, index) => ({ ...row, id: index + 1 }))
    : []

  const exceptionRows = entry
    ? [...entry.pack.exceptions].sort((a, b) => a.time.localeCompare(b.time))
    : []

  const handlePickDate = (date: Date | null) => {
    setPickedDate(date)
    if (date) setDateLabel(opsDateLabelOf(date))
  }

  const selectEntry = (item: IOpsShiftReviewEntry) => {
    setDateLabel(item.dateLabel)
    setShiftName(item.shiftName)
    setPickedDate(null)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const reviewColumns: IHonestTableColumn<IOpsShiftReviewEntry>[] = [
    {
      id: 'review',
      header: 'Review',
      minWidth: '10rem',
      cell: (item) => (
        <ReviewCell>
          <ReviewId $active={entry?.id === item.id}>#{item.id}</ReviewId>
          <BodyText weight='600'>{item.dateLabel}</BodyText>
        </ReviewCell>
      ),
    },
    {
      id: 'shift',
      header: 'Shift',
      minWidth: '8rem',
      cell: (item) => <BodyText>{item.shiftName}</BodyText>,
    },
    {
      id: 'tonnes',
      header: 'Tonnes',
      minWidth: '12rem',
      cell: (item) => (
        <Flex center gap='0.5rem'>
          <BodyText>
            {formatNumbersForDisplay(item.tonnes)} / {formatNumbersForDisplay(item.target)}
          </BodyText>
          <TonnesPct $tone={tonnesTone(item.tonnes, item.target)}>
            {Math.round((item.tonnes / item.target) * 100)}%
          </TonnesPct>
        </Flex>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      minWidth: '8rem',
      cell: (item) =>
        item.locked ? (
          <StatusPill status='neutral'>Locked</StatusPill>
        ) : (
          <StatusPill status='info'>Open</StatusPill>
        ),
    },
  ]

  if (!USE_OPS_MOCKS) {
    return (
      <>
        <NavBar />
        <PageWrapper>
          <Head1 style={{ fontSize: '1.5rem', lineHeight: 1.3 }} color={tokens.text.primary}>
            Shift review
          </Head1>
          <NoData text='The shift review generates automatically at shift end.' />
        </PageWrapper>
      </>
    )
  }

  return (
    <>
      <NavBar />
      <PageWrapper>
        <Flex center gap='0.75rem' wrap>
          <Head1 style={{ fontSize: '1.5rem', lineHeight: 1.3 }} color={tokens.text.primary}>
            Shift review
          </Head1>
          <DemoBadge />
          {entry && !entry.locked && (
            <StatusPill status='warning'>OPEN — locks at shift end</StatusPill>
          )}
          <Button variant='black' size='sm' onClick={() => window.print()}>
            Print / PDF
          </Button>
        </Flex>
        <SubtitleWrap>
          <BodyText grey>
            {entry
              ? entry.locked
                ? `${entry.pack.shiftName} · ${entry.pack.dateLabel} · locks with the handover`
                : `${entry.pack.shiftName} · ${entry.pack.dateLabel} · in progress — provisional until shift end`
              : `${shiftName} · ${dateLabel}`}
          </BodyText>
          {entry && <OtherText grey>{entry.pack.subtitleNote ?? DAY_AVG_NOTE}</OtherText>}
        </SubtitleWrap>
        <Flex mt='16' center gap='0.75rem' wrap>
          <TabsContainer>
            {SHIFT_PILLS.map((name) => (
              <Tabs key={name} active={shiftName === name} onClick={() => setShiftName(name)}>
                {name}
              </Tabs>
            ))}
          </TabsContainer>
          <CalendarInput
            name='reviewDay'
            placeholder='Jump to date'
            width='12rem'
            nomarg
            defaultValue={pickedDate}
            minDate={PICKER_MIN_DATE}
            maxDate={new Date()}
            onChange={handlePickDate}
          />
        </Flex>
        <ReviewStack>
          {entry ? (
            <>
              <Container>
                <ContainerHeader>
                  <TitleText>Scorecard</TitleText>
                </ContainerHeader>
                <ContainerContent>
                  <HonestTable columns={scorecardColumns} data={scorecardRows} keyField='id' />
                </ContainerContent>
              </Container>
              {entry.pack.waterfall.length ? (
                <Container>
                  <ContainerHeader>
                    <TitleText>Loss waterfall</TitleText>
                  </ContainerHeader>
                  <ContainerContent>
                    <LossWaterfall bars={entry.pack.waterfall} />
                  </ContainerContent>
                </Container>
              ) : (
                <InfoNote>
                  <OtherText color={tokens.status.info.fg}>
                    Loss attribution runs at shift end.
                  </OtherText>
                </InfoNote>
              )}
              <Container>
                <ContainerHeader>
                  <TitleText>Exceptions</TitleText>
                </ContainerHeader>
                <ContainerContent>
                  <ExceptionList>
                    {exceptionRows.map((row) => {
                      const handleOpen = () => {
                        if (row.linkPath) navigate(row.linkPath)
                      }
                      return (
                        <ExceptionRow
                          key={row.id}
                          $clickable={!!row.linkPath}
                          onClick={row.linkPath ? handleOpen : undefined}
                        >
                          <ExceptionTime>{row.time}</ExceptionTime>
                          <ExceptionDot $tone={row.severity} />
                          <BodyText weight='600'>{row.text}</BodyText>
                          {row.linkPath && <OpenLink>Open ›</OpenLink>}
                        </ExceptionRow>
                      )
                    })}
                  </ExceptionList>
                </ContainerContent>
              </Container>
              <Container>
                <ContainerHeader>
                  <TitleText>People</TitleText>
                </ContainerHeader>
                <ContainerContent>
                  <PeopleList>
                    {entry.pack.peopleNotes.map((note, index) => {
                      const isTopLoader = note.startsWith(TOP_LOADER_PREFIX)
                      return (
                        <PeopleRow key={index}>
                          <PeopleBullet $accent={isTopLoader} />
                          {isTopLoader ? (
                            <>
                              <StatusPill status='success'>Top loader</StatusPill>
                              <BodyText>{note.slice(TOP_LOADER_PREFIX.length).trim()}</BodyText>
                            </>
                          ) : (
                            <BodyText>{note}</BodyText>
                          )}
                        </PeopleRow>
                      )
                    })}
                  </PeopleList>
                </ContainerContent>
              </Container>
              {entry.locked && (
                <LockNoteWrap>
                  <OtherText grey>
                    This pack locked when the handover was acknowledged. Corrections are new events.
                  </OtherText>
                </LockNoteWrap>
              )}
            </>
          ) : (
            <NoData text='No review filed for this shift.' />
          )}
          <Container>
            <ContainerHeader>
              <TitleText>All shift reviews</TitleText>
            </ContainerHeader>
            <ContainerContent>
              <HonestTable
                columns={reviewColumns}
                data={OPS_SHIFT_REVIEWS}
                keyField='id'
                onClick={selectEntry}
                emptyMessage='No reviews filed yet.'
              />
            </ContainerContent>
          </Container>
        </ReviewStack>
      </PageWrapper>
    </>
  )
}
